import React, { useState, useEffect } from 'react';
import api from '../config/api';

function CreateInventoryModal({ onClose, onCreate }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    location_type_id: ''
  });

  const [itemTypes, setItemTypes] = useState([]);
  const [selectedItemTypes, setSelectedItemTypes] = useState([]);
  const [defaultItemTypeId, setDefaultItemTypeId] = useState('');
  const [locationTypes, setLocationTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchItemTypes();
    fetchLocationTypes();
  }, []);

  const fetchItemTypes = async () => {
    try {
      const response = await api.get('/item-types');
      setItemTypes(response.data);
    } catch (error) {
      console.error('Error fetching item types:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocationTypes = async () => {
    try {
      const response = await api.get('/location-types');
      setLocationTypes(response.data);
    } catch (error) {
      console.error('Error fetching location types:', error);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleItemTypeToggle = (itemTypeId) => {
    if (selectedItemTypes.includes(itemTypeId)) {
      const newSelected = selectedItemTypes.filter(id => id !== itemTypeId);
      setSelectedItemTypes(newSelected);
      
      // If we removed the default, set a new default
      if (defaultItemTypeId === itemTypeId) {
        setDefaultItemTypeId(newSelected.length > 0 ? newSelected[0] : '');
      }
    } else {
      const newSelected = [...selectedItemTypes, itemTypeId];
      setSelectedItemTypes(newSelected);
      
      // If this is the first selected, make it default
      if (newSelected.length === 1) {
        setDefaultItemTypeId(itemTypeId);
      }
    }
  };

  const handleDefaultChange = (itemTypeId) => {
    setDefaultItemTypeId(itemTypeId);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim() || selectedItemTypes.length === 0) return;
    
    const submitData = {
      ...formData,
      item_types: selectedItemTypes,
      default_item_type_id: defaultItemTypeId
    };
    
    onCreate(submitData);
  };

  const getItemTypeById = (id) => {
    return itemTypes.find(type => type.id === parseInt(id));
  };

  const defaultItemType = getItemTypeById(defaultItemTypeId);
  const selectedLocationType = locationTypes.find(type => type.id === parseInt(formData.location_type_id));

  if (loading) {
    return (
      <div className="modal">
        <div className="modal-content">
          <p>Loading item types...</p>
        </div>
      </div>
    );
  }

  if (itemTypes.length === 0) {
    return (
      <div className="modal">
        <div className="modal-content">
          <h2>No Item Types Available</h2>
          <p>You need to create at least one item type before you can create an inventory.</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem' }}>
            <button type="button" className="btn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal">
      <div className="modal-content">
        <h2>Create New Inventory</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Inventory Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              placeholder="e.g., Office Electronics, Warehouse Tools..."
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="3"
              placeholder="Describe this specific inventory..."
            />
          </div>

          <div className="form-group">
            <label>Item Types * (Select one or more)</label>
            <div style={{ 
              border: '1px solid #ddd', 
              borderRadius: '4px', 
              maxHeight: '200px', 
              overflowY: 'auto',
              padding: '0.5rem'
            }}>
              {itemTypes.map(itemType => (
                <div key={itemType.id} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem',
                  padding: '0.5rem',
                  borderBottom: '1px solid #eee'
                }}>
                  <input
                    type="checkbox"
                    id={`itemtype-${itemType.id}`}
                    checked={selectedItemTypes.includes(itemType.id)}
                    onChange={() => handleItemTypeToggle(itemType.id)}
                  />
                  <label 
                    htmlFor={`itemtype-${itemType.id}`}
                    style={{ 
                      flex: 1, 
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <strong>{itemType.name}</strong>
                      {itemType.description && (
                        <div style={{ fontSize: '0.8rem', color: '#666' }}>
                          {itemType.description}
                        </div>
                      )}
                      <div style={{ fontSize: '0.8rem', color: '#888' }}>
                        {itemType.fields?.length || 0} fields
                      </div>
                    </div>
                    {selectedItemTypes.includes(itemType.id) && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="radio"
                          name="defaultItemType"
                          id={`default-${itemType.id}`}
                          checked={defaultItemTypeId === itemType.id}
                          onChange={() => handleDefaultChange(itemType.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <label 
                          htmlFor={`default-${itemType.id}`}
                          style={{ fontSize: '0.8rem', color: '#0066cc', cursor: 'pointer' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          Default
                        </label>
                      </div>
                    )}
                  </label>
                </div>
              ))}
            </div>
            {selectedItemTypes.length === 0 && (
              <div style={{ fontSize: '0.8rem', color: '#e74c3c', marginTop: '0.5rem' }}>
                Please select at least one item type
              </div>
            )}
            {selectedItemTypes.length > 0 && (
              <div style={{ fontSize: '0.8rem', color: '#27ae60', marginTop: '0.5rem' }}>
                {selectedItemTypes.length} item type{selectedItemTypes.length > 1 ? 's' : ''} selected
                {defaultItemType && ` • Default: ${defaultItemType.name}`}
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Location Type (Optional)</label>
            <select
              name="location_type_id"
              value={formData.location_type_id}
              onChange={handleInputChange}
            >
              <option value="">-- No location tracking --</option>
              {locationTypes.map(locationType => (
                <option key={locationType.id} value={locationType.id}>
                  {locationType.name} ({locationType.locations?.length || 0} locations)
                </option>
              ))}
            </select>
          </div>

          {defaultItemType && (
            <div className="form-group">
              <label>Default Item Type Preview</label>
              <div style={{ border: '1px solid #ddd', padding: '1rem', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
                <p><strong>{defaultItemType.name}</strong> <span style={{ fontSize: '0.8rem', color: '#0066cc' }}>(Default)</span></p>
                {defaultItemType.description && <p style={{ fontSize: '0.9rem', color: '#666' }}>{defaultItemType.description}</p>}
                {defaultItemType.fields && defaultItemType.fields.length > 0 ? (
                  <div>
                    <p style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>New items will have these fields by default:</p>
                    {defaultItemType.fields.slice(0, 5).map((field, index) => (
                      <div key={index} style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                        • <strong>{field.name}</strong> ({field.type})
                        {field.required && <span style={{ color: '#e74c3c' }}> *</span>}
                        {field.description && ` - ${field.description}`}
                      </div>
                    ))}
                    {defaultItemType.fields.length > 5 && (
                      <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem' }}>
                        +{defaultItemType.fields.length - 5} more fields
                      </div>
                    )}
                  </div>
                ) : (
                  <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666' }}>No fields defined for this item type.</p>
                )}
              </div>
            </div>
          )}

          {selectedLocationType && (
            <div className="form-group">
              <label>Location Tracking Preview</label>
              <div style={{ border: '1px solid #ddd', padding: '1rem', borderRadius: '4px', backgroundColor: '#f0f8ff' }}>
                <p><strong>{selectedLocationType.name}</strong></p>
                <p style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>Items will have a Location field with these options:</p>
                {selectedLocationType.locations && selectedLocationType.locations.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {selectedLocationType.locations.slice(0, 8).map((location, index) => (
                      <span key={index} style={{ 
                        background: '#e3f2fd', 
                        padding: '0.25rem 0.5rem', 
                        borderRadius: '4px', 
                        fontSize: '0.9rem' 
                      }}>
                        {location}
                      </span>
                    ))}
                    {selectedLocationType.locations.length > 8 && (
                      <span style={{ fontSize: '0.9rem', color: '#666' }}>
                        +{selectedLocationType.locations.length - 8} more
                      </span>
                    )}
                  </div>
                ) : (
                  <p style={{ fontSize: '0.9rem', color: '#666' }}>No locations defined for this location type.</p>
                )}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn" 
              disabled={!formData.name.trim() || selectedItemTypes.length === 0}
            >
              Create Inventory
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateInventoryModal;